import type { ChatPresenceParticipant, ChatPresenceRole } from '@/types/chat-presence';

/** `"false"` is truthy in JS — common JSON / serialization mistake from APIs. */
function coercePresenceBoolean(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0' || s === '') return false;
  }
  return Boolean(v);
}

function parseOptionalSocketCount(v: unknown): number | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseInt(v, 10);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

/**
 * Normalize one REST participant row (camelCase / snake_case / string booleans).
 */
export function normalizePresenceParticipant(raw: unknown): ChatPresenceParticipant | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const roleRaw = String(o.role ?? o.role_type ?? '').toUpperCase();
  if (roleRaw !== 'LEAD' && roleRaw !== 'STUDENT' && roleRaw !== 'ADMIN') return null;

  const userId = String(o.userId ?? o.user_id ?? '');
  const socketCount = parseOptionalSocketCount(o.socketCount ?? o.socket_count);
  const isPresent = coercePresenceBoolean(o.isPresent ?? o.is_present);
  const lastSeenRaw = o.lastSeenAt ?? o.last_seen_at;
  const lastSeenAt = typeof lastSeenRaw === 'string' ? lastSeenRaw : undefined;

  return {
    userId,
    role: roleRaw as ChatPresenceRole,
    isPresent,
    lastSeenAt,
    ...(socketCount !== undefined ? { socketCount } : {}),
  };
}

export function normalizePresenceParticipants(raw: unknown[]): ChatPresenceParticipant[] {
  return raw.map(normalizePresenceParticipant).filter((p): p is ChatPresenceParticipant => p !== null);
}

/**
 * Extract `data.participants` or top-level `participants` from admin presence JSON.
 * Returns `[]` when the server omits participants on a success envelope (no LEAD row → offline).
 */
export function participantsFromPresenceApiResponse(res: unknown): ChatPresenceParticipant[] | null {
  if (!res || typeof res !== 'object') return null;
  const o = res as Record<string, unknown>;
  if (o.success === false) return null;

  let raw: unknown[] | null = null;
  if (typeof o.data === 'object' && o.data !== null) {
    const d = o.data as Record<string, unknown>;
    if (Array.isArray(d.participants)) raw = d.participants;
  }
  if (!raw && Array.isArray(o.participants)) raw = o.participants;

  const explicitOk = o.statusCode === 200 || o.success === true;

  if (raw !== null) {
    return normalizePresenceParticipants(raw);
  }

  if (explicitOk) return [];
  return null;
}

export function findParticipantByRole(
  participants: ChatPresenceParticipant[],
  role: ChatPresenceRole
): ChatPresenceParticipant | undefined {
  const want = role.toUpperCase();
  return participants.find((p) => String(p.role).toUpperCase() === want);
}

/**
 * Prefer socketCount when the API sends it: isPresent is debounced (~15s) on disconnect,
 * so the visitor/student line would otherwise stay "connected" briefly after they close.
 * Fall back to isPresent if socketCount is missing.
 */
export function isParticipantLive(p: ChatPresenceParticipant | undefined): boolean {
  if (!p) return false;
  const sc = parseOptionalSocketCount(p.socketCount);
  if (sc !== undefined) return sc > 0;
  return coercePresenceBoolean(p.isPresent);
}
